import { useMemo } from "react";
import {
  useGetAllInvestmentEntitiesQuery,
  useGetPositionsStatusQuery,
  useGetTotalPositionsInEntityQuery,
} from "../../../graphql/supergraphHooks";
import { toGlobalId } from "../../../utils/graphql_utils";
import { COMPLIANCE_PROGRAMS } from "../constants";
import { isNoEntityAccessError } from "../utils";

export type PositionStatusCounts = {
  compliant: number;
  reviewNotStarted: number;
  inJSQReview: number;
  inLPSubmission: number;
  pendingDivestment: number;
};

type UsePositionStatusCountsArgs = {
  arenaId: number;
  entityId?: number | null;
};

export const usePositionStatusCounts = ({ arenaId, entityId }: UsePositionStatusCountsArgs) => {
  const statusResult = useGetPositionsStatusQuery({
    variables: { arenaId, entityId },
  });

  const allEntitiesResult = useGetAllInvestmentEntitiesQuery({
    skip: entityId != null,
    variables: { compliancePrograms: COMPLIANCE_PROGRAMS },
  });

  const entityIdsForPositions = useMemo(() => {
    if (entityId != null) return [toGlobalId("AccountObjectType", entityId)];
    const edges = allEntitiesResult.data?.investmentEntities?.edges ?? [];
    if (edges.length === 0) return undefined;
    return edges.map((e) => e.node.id);
  }, [entityId, allEntitiesResult.data]);

  const totalResult = useGetTotalPositionsInEntityQuery({
    variables: {
      positionFilters: {
        arenaIds: [toGlobalId("ArenaObjectType", arenaId)],
        entityIds: entityIdsForPositions,
      },
    },
    skip:
      entityId != null ? false : allEntitiesResult.loading || !entityIdsForPositions?.length,
  });

  const summary = statusResult.data?.complianceBucketCounts;
  const compliant = summary?.compliantCount ?? 0;
  const inLPSubmission = summary?.lpSubmissionCount ?? 0;
  const inJSQReview = summary?.juniperSquareReviewCount ?? 0;
  const pendingDivestment = summary?.pendingDivestmentCount ?? 0;
  const total = totalResult.data?.positions?.count ?? 0;
  const reviewNotStarted = Math.max(
    0,
    total - compliant - inLPSubmission - inJSQReview - pendingDivestment,
  );

  const counts: PositionStatusCounts = {
    compliant,
    reviewNotStarted,
    inJSQReview,
    inLPSubmission,
    pendingDivestment,
  };

  const hasNoEntityAccess = isNoEntityAccessError(statusResult.error);

  // The authz error is handled with a dedicated page rather than the generic
  // QueryLoader error state, so it is stripped from the loader results.
  const visibleStatusResult = hasNoEntityAccess
    ? { ...statusResult, error: undefined }
    : statusResult;

  const queryResults =
    entityId == null
      ? [visibleStatusResult, allEntitiesResult, totalResult]
      : [visibleStatusResult, totalResult];

  const hasComplianceEntities =
    entityId != null || (allEntitiesResult.data?.investmentEntities?.edges ?? []).length > 0;

  return {
    counts,
    total,
    hasPositions: total > 0,
    hasComplianceEntities,
    hasNoEntityAccess,
    hasPendingDivestment: pendingDivestment > 0,
    queryResults,
  };
};
